import { fromJS, List, Set } from 'immutable';
import getIn from 'lodash/get';

import { type ValueOf } from 'src/types';

import { ApiActions } from '../constants/ApiActions';
import { Keys as FeedbackKeys } from '../constants/Feedback';
import { makeSparseArray } from '../lib/ArrayUtils';
import { Map } from '../lib/ImmutableUtils';
import { createReducer } from '../lib/ReducerUtils';

const INITIAL_STATE = Map(
  {} as Record<string /* stationId*/, Record<ValueOf<typeof FeedbackKeys>, Set<$TSFixMe>>>,
);
export type FeedbackT3State = typeof INITIAL_STATE;

// Feedback Reducer to facilitate random access fetching
export default createReducer(INITIAL_STATE, {
  [ApiActions.GET_STATION_FEEDBACK_ATTEMPT]: onGetStationFeedbackAttempt,
  [ApiActions.GET_STATION_FEEDBACK_SUCCESS]: onGetStationFeedbackSuccess,
  [ApiActions.GET_STATION_FEEDBACK_FAIL]: onGetStationFeedbackFail,
  [ApiActions.DELETE_FEEDBACK_SUCCESS]: onDeleteFeedbackSuccess,
  [ApiActions.ACTION_REMOVE_THUMB_SUCCESS]: onDeleteFeedbackSuccess,
});

// Case Functions
function onGetStationFeedbackAttempt(state: FeedbackT3State, action: $TSFixMe): FeedbackT3State {
  const stationId = action.request.content?.stationId;
  const startIndex = action.request.content?.startIndex;
  if (stationId && startIndex >= 0) {
    const previous = state.getIn([stationId, FeedbackKeys.FETCHING_OFFSETS]) || Set();
    return state.setIn([stationId, FeedbackKeys.FETCHING_OFFSETS], previous.add(startIndex));
  }
  return state;
}

function onGetStationFeedbackSuccess(state: FeedbackT3State, action: $TSFixMe): FeedbackT3State {
  const stationId = action.request.content.stationId;
  const total = action.response.result.total;
  const startIndex = action.params.content
    ? action.params.content.startIndex
    : action.request.content.startIndex;
  const feedbackEntities = fromJS(action.response.entities.feedback);
  const feedbackIds = fromJS(action.response.result.feedback);
  const previousFetching = state.getIn([stationId, FeedbackKeys.FETCHING_OFFSETS]) || Set();
  if (stationId && startIndex >= 0 && total >= 0 && feedbackEntities && feedbackIds) {
    const oldTracks = state.getIn([stationId, FeedbackKeys.THUMBED_TRACKS]) || List();
    if (action.mostRecent) {
      const pandoraId = feedbackEntities.getIn([feedbackIds.get(0), 'pandoraId'], null);
      const newMap = pandoraId ? Map({ pandoraId }) : oldTracks.get(0, Map());
      return state.setIn([stationId, FeedbackKeys.THUMBED_TRACKS], oldTracks.unshift(newMap));
    }
    const sparseTracks = makeSparseArray(total, i => {
      const indexBeforeOffset = i - startIndex;
      const pandoraId = feedbackEntities.getIn(
        [feedbackIds.get(indexBeforeOffset), 'pandoraId'],
        null,
      );
      const newMap = pandoraId ? Map({ pandoraId }) : oldTracks.get(i, Map());
      return indexBeforeOffset < 0 ? oldTracks.get(i) : newMap;
    });
    const previousFetched = state.getIn([stationId, FeedbackKeys.FETCHED_OFFSETS]) || Set();
    return state
      .setIn([stationId, FeedbackKeys.FETCHED_OFFSETS], previousFetched.add(startIndex))
      .setIn([stationId, FeedbackKeys.FETCHING_OFFSETS], previousFetching.remove(startIndex))
      .setIn([stationId, FeedbackKeys.THUMBED_TRACKS], List(sparseTracks));
  }
  return state.setIn(
    [stationId, FeedbackKeys.FETCHING_OFFSETS],
    previousFetching.remove(startIndex),
  );
}

function onGetStationFeedbackFail(state: FeedbackT3State, action: $TSFixMe): FeedbackT3State {
  const stationId = action.stationId;
  const startIndex = getIn(action, 'error.action.content.startIndex');
  if (stationId && startIndex >= 0) {
    const previousFetching = state.getIn([stationId, FeedbackKeys.FETCHING_OFFSETS]) || Set();
    return state.setIn(
      [stationId, FeedbackKeys.FETCHING_OFFSETS],
      previousFetching.remove(startIndex),
    );
  }
  return state;
}

function onDeleteFeedbackSuccess(state: FeedbackT3State, action: $TSFixMe): FeedbackT3State {
  const stationId = action.stationId;
  const trackId =
    action.type === ApiActions.ACTION_REMOVE_THUMB_SUCCESS
      ? action.pandoraId
      : action.response.pandoraId;
  if (stationId && trackId) {
    const oldTracks = state.getIn([stationId, FeedbackKeys.THUMBED_TRACKS]) || List();
    // @ts-expect-error
    const indexToDelete = oldTracks.findIndex(track => track.get('pandoraId') === trackId);
    if (indexToDelete >= 0) {
      return state.setIn([stationId, FeedbackKeys.THUMBED_TRACKS], oldTracks.delete(indexToDelete));
    }
  }
  return state;
}
